var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { flexRender, getCoreRowModel, useReactTable, } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { ErrorOverlay, LoadingOverlay } from 'shared/components/features/LoadingOverlay';
import { Table as ChakraTable, Thead, Tbody, Tr, Th, Td, Flex, Spinner, } from 'shared/components/ui';
import { usePreservedTableContext } from '../../contexts';
import { PaginationIndex } from '../PaginationIndex';
import { SortableTableHeader } from '../SortableTable/sortableTableHeader';
var PaginatedTable = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var columns = _a.columns, query = _a.query, pageSizeOptions = _a.pageSizeOptions, preservedTableId = _a.preservedTableId, noData = _a.noData;
    var _k = usePreservedTableContext(), preservedTables = _k.preservedTables, setTableState = _k.setTableState;
    var preservedTable = preservedTableId ? preservedTables[preservedTableId] : null;
    var _l = useState({
        pageIndex: 0,
        pageSize: (_b = preservedTable === null || preservedTable === void 0 ? void 0 : preservedTable.pageSize) !== null && _b !== void 0 ? _b : pageSizeOptions[0],
    }), _m = _l[0], pageIndex = _m.pageIndex, pageSize = _m.pageSize, setPagination = _l[1];
    var _o = useState((_d = (_c = preservedTable === null || preservedTable === void 0 ? void 0 : preservedTable.sortBy) === null || _c === void 0 ? void 0 : _c.map(function (sr) { return ({ id: sr.id, desc: !!sr.desc }); })) !== null && _d !== void 0 ? _d : []), sorting = _o[0], setSorting = _o[1];
    var getSortingPayload = function () {
        if (sorting.length !== 0) {
            var sortByPayload = [
                {
                    column: sorting[0].id,
                    direction: sorting[0].desc ? 'desc' : 'asc',
                },
            ];
            return sortByPayload;
        }
        else
            return undefined;
    };
    var _p = query({
        limit: pageSize,
        offset: pageSize * pageIndex,
        sort_by: getSortingPayload(),
    }), queryRes = _p.data, isLoading = _p.isLoading, isFetching = _p.isFetching, isError = _p.isError, refetch = _p.refetch, isPreviousData = _p.isPreviousData;
    useEffect(function () {
        refetch();
    }, [sorting]);
    var defaultData = useMemo(function () { return []; }, []);
    var pagination = useMemo(function () { return ({
        pageIndex: pageIndex,
        pageSize: pageSize,
    }); }, [pageIndex, pageSize]);
    var table = useReactTable({
        data: (_e = queryRes === null || queryRes === void 0 ? void 0 : queryRes.data) !== null && _e !== void 0 ? _e : defaultData,
        columns: columns,
        state: {
            pagination: pagination,
            sorting: sorting,
        },
        pageCount: ((_f = queryRes === null || queryRes === void 0 ? void 0 : queryRes.paging) === null || _f === void 0 ? void 0 : _f.pages) || 0,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
    });
    var tableState = table.getState();
    useEffect(function () {
        if (preservedTableId) {
            setTableState(preservedTableId, {
                pageSize: table.getState().pagination.pageSize,
                sortBy: table.getState().sorting,
            });
        }
    }, [
        preservedTableId,
        tableState.pagination.pageIndex,
        tableState.pagination.pageSize,
        tableState.sorting,
    ]);
    if (noData && !isPreviousData && ((_g = queryRes === null || queryRes === void 0 ? void 0 : queryRes.data) === null || _g === void 0 ? void 0 : _g.length) === 0)
        return _jsx(_Fragment, { children: noData });
    if (isLoading)
        return _jsx(Spinner, {});
    return (_jsx(_Fragment, { children: _jsxs(ErrorOverlay, __assign({ enabled: isError }, { children: [_jsx(Flex, __assign({ justifyContent: "flex-end", m: 3, mb: 2.5 }, { children: _jsx(PaginationIndex, { pageIndex: table.getState().pagination.pageIndex, total: ((_h = queryRes === null || queryRes === void 0 ? void 0 : queryRes.paging) === null || _h === void 0 ? void 0 : _h.total) || 0, pageSize: table.getState().pagination.pageSize, pageSizeOptions: pageSizeOptions, canNextPage: table.getCanNextPage(), canPreviousPage: table.getCanPreviousPage(), setPageSize: table.setPageSize, nextPage: table.nextPage, previousPage: table.previousPage }) })), _jsx(LoadingOverlay, __assign({ enabled: isFetching && isPreviousData }, { children: _jsxs(ChakraTable, __assign({ "data-testid": "table", size: "sm" }, { children: [_jsx(Thead, { children: table.getHeaderGroups().map(function (headerGroup) { return (_jsx(Tr, __assign({ border: 0 }, { children: headerGroup.headers.map(function (header) {
                                        var _a, _b, _c, _d, _e;
                                        return (_jsx(Th, __assign({ h: 'full', userSelect: "none", w: header.getSize(), _hover: {
                                                cursor: header.column.columnDef.enableSorting === false ? 'auto' : 'pointer',
                                            }, onClick: header.column.getToggleSortingHandler() }, { children: _jsx(Flex, { children: _jsx(SortableTableHeader, { title: header.column.columnDef.header, isSorted: !!header.column.getIsSorted(), isSortedDesc: header.column.getIsSorted() === 'desc', icon: (_a = header.column.columnDef) === null || _a === void 0 ? void 0 : _a.headerIcon, popoverProps: ((_b = header.column.columnDef) === null || _b === void 0 ? void 0 : _b.headerTooltipTitle) &&
                                                        ((_c = header.column.columnDef) === null || _c === void 0 ? void 0 : _c.headerTooltipDescription)
                                                        ? {
                                                            title: (_d = header.column.columnDef) === null || _d === void 0 ? void 0 : _d.headerTooltipTitle,
                                                            description: (_e = header.column.columnDef) === null || _e === void 0 ? void 0 : _e.headerTooltipDescription,
                                                        }
                                                        : undefined }) }) }), header.id));
                                    }) }), headerGroup.id)); }) }), _jsx(Tbody, { children: table.getRowModel().rows.map(function (row) {
                                    return (_jsx(Tr, { children: row.getVisibleCells().map(function (cell) {
                                            return (_jsx(Td, __assign({ ml: 2 }, { children: flexRender(cell.column.columnDef.cell, cell.getContext()) }), cell.id));
                                        }) }, row.id));
                                }) })] })) })), _jsx(Flex, __assign({ justifyContent: "flex-end", m: 3, mb: 0 }, { children: _jsx(PaginationIndex, { pageIndex: table.getState().pagination.pageIndex, total: ((_j = queryRes === null || queryRes === void 0 ? void 0 : queryRes.paging) === null || _j === void 0 ? void 0 : _j.total) || 0, pageSize: table.getState().pagination.pageSize, pageSizeOptions: pageSizeOptions, canNextPage: table.getCanNextPage(), canPreviousPage: table.getCanPreviousPage(), setPageSize: table.setPageSize, nextPage: table.nextPage, previousPage: table.previousPage }) }))] })) }));
};
export { PaginatedTable };
